import React, { useState, useEffect, useActionState } from 'react';
import { api } from '../api/client.js';

const FIELDS = [
  { key: 'q_dep_count', label: 'Dependents', type: 'number' },
  { key: 'q_par_tax_form', label: 'Parent Tax Form', type: 'select', options: ['1040', '1040A', '1040EZ', 'foreign', 'none'] },
  { key: 'q_par_agi', label: 'Parent AGI ($)', type: 'number' },
  { key: 'q_student_dep', label: 'Student Dependent', type: 'select', options: ['yes', 'no'] },
  { key: 'VarParentDataRequired', label: 'Parent Data Required', type: 'select', options: ['yes', 'no'] },
  { key: 'q_par_marital_status', label: 'Parent Marital Status', type: 'select', options: ['married', 'divorced', 'separated', 'widowed', 'single', 'unmarried'] },
  { key: 'q_household_size', label: 'Household Size', type: 'number' },
  { key: 'q_student_income', label: 'Student Income ($)', type: 'number' }
];

const INITIAL = Object.fromEntries(FIELDS.map((f) => [f.key, '']));

function compact(p) {
  const out = {};
  for (const [k, v] of Object.entries(p)) {
    if (v === '' || v == null) continue;
    const n = Number(v);
    out[k] = !isNaN(n) && /^-?\d+(\.\d+)?$/.test(String(v)) ? n : v;
  }
  return out;
}

export default function DiffView() {
  const [specs, setSpecs] = useState([]);
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [profile, setProfile] = useState(INITIAL);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.listSpecs().then((data) => {
      const list = Array.isArray(data) ? data : data.items ?? data.specs ?? [];
      setSpecs(list);
      if (list.length > 0) setVersionA(list[0].label ?? list[0]);
      if (list.length > 1) setVersionB(list[1].label ?? list[1]);
    }).catch(() => {});
  }, []);

  const [state, submit, isPending] = useActionState(async () => {
    try {
      const data = await api.diff(versionA, versionB, compact(profile));
      setResult(data);
      return null;
    } catch (e) { return { error: e.message }; }
  }, null);

  const handleField = (key, value) => setProfile((p) => ({ ...p, [key]: value }));
  const specLabels = specs.map((s) => (typeof s === 'string' ? s : s.label));

  return (
    <>
      <h1>Version Diff</h1>
      <div className="cb-card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
          <div className="cb-field">
            <label>Version A</label>
            <select value={versionA} onChange={(e) => setVersionA(e.target.value)}>
              {specLabels.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="cb-field">
            <label>Version B</label>
            <select value={versionB} onChange={(e) => setVersionB(e.target.value)}>
              {specLabels.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-gray-5)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>Profile</legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            {FIELDS.map(({ key, label, type, options }) => (
              <div key={key} className="cb-field" style={{ marginBottom: 0 }}>
                <label htmlFor={`diff-${key}`}>{label}</label>
                {type === 'select' ? (
                  <select id={`diff-${key}`} value={profile[key]} onChange={(e) => handleField(key, e.target.value)}>
                    <option value="">—</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input id={`diff-${key}`} type="number" value={profile[key]}
                    onChange={(e) => handleField(key, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        </fieldset>
        <div style={{ marginTop: 16 }}>
          <button className="cb-btn primary" onClick={submit} disabled={isPending || !versionA || !versionB}>
            {isPending ? 'Running…' : 'Run Diff'}
          </button>
        </div>
        {state?.error && <div className="cb-alert" role="alert" style={{ marginTop: 12 }}>{state.error}</div>}
      </div>

      {result && (
        <div className="cb-card">
          <h2>Diff: {versionA} → {versionB}</h2>
          {(result.sections ?? result.diff ?? []).length === 0 ? (
            <p className="cb-muted">No differences.</p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {(result.sections ?? result.diff ?? []).map((s) => {
                const id = s.id ?? s.sectionId ?? s.name;
                const cls = s.status === 'added' ? 'cb-added' : s.status === 'removed' ? 'cb-removed' : 'cb-changed';
                return (
                  <li key={id} className={cls} style={{ padding: '6px 0', borderBottom: '1px solid var(--cb-gray-2)', fontSize: 13 }}>
                    <strong>{s.label ?? id}</strong> <span style={{ fontSize: 11 }}>({s.status})</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
